import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Brain,
  Loader2,
  Star,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  Clock,
  ChevronDown,
  ChevronUp,
  Target,
  Wallet,
  Calendar,
  BarChart3,
  RefreshCw,
  Info,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { B8ildLogoLoader } from '@/components/ui/B8ildLogoLoader';
import { useAnalyseIA, type AnalyseIAResult } from '@/hooks/useAnalyseIA';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { QuotaExceededBanner } from '@/components/ui/QuotaExceededBanner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ScoreEvolutionChart } from '@/components/charts/ScoreEvolutionChart';
import { AnalyseHistoriqueModal } from '@/components/chantiers/AnalyseHistoriqueModal';

interface AnalyseIATabProps {
  chantierId: string;
  entrepriseId: string;
  chantierData: any;
}

// Couleur selon l'indicateur
function getIndicateurStyle(indicateur: string): { color: string; bg: string; label: string } {
  switch (indicateur) {
    case 'exceptionnel':
      return { color: 'text-purple-500', bg: 'bg-purple-500/10', label: 'Exceptionnel' };
    case 'excellent':
      return { color: 'text-green-500', bg: 'bg-green-500/10', label: 'Excellent' };
    case 'tres_bien':
      return { color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: 'Très bien' };
    case 'bien':
      return { color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'Bien' };
    case 'correct':
      return { color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'Correct' };
    case 'attention':
      return { color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'Attention' };
    case 'critique':
      return { color: 'text-red-500', bg: 'bg-red-500/10', label: 'Critique' };
    default:
      return { color: 'text-muted-foreground', bg: 'bg-muted/50', label: indicateur };
  }
}

// Icône selon le statut planning
function getPlanningIcon(statut: string) {
  switch (statut) {
    case 'en_avance':
      return <TrendingUp className="w-4 h-4 text-green-500" />;
    case 'a_lheure':
      return <CheckCircle2 className="w-4 h-4 text-blue-500" />;
    case 'leger_retard':
      return <Clock className="w-4 h-4 text-orange-500" />;
    case 'retard_critique':
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    default:
      return <Clock className="w-4 h-4 text-muted-foreground" />;
  }
}

// Couleur badge priorité
function getPrioriteBadgeVariant(priorite: string): 'default' | 'secondary' | 'destructive' {
  switch (priorite) {
    case 'haute':
      return 'destructive';
    case 'moyenne':
      return 'default';
    case 'basse':
      return 'secondary';
    default:
      return 'secondary';
  }
}

// Couleur niveau alerte
function getNiveauStyle(niveau: string): { icon: React.ReactNode; bg: string; border: string } {
  switch (niveau) {
    case 'critique':
      return {
        icon: <AlertTriangle className="w-4 h-4 text-red-500" />,
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
      };
    case 'attention':
      return {
        icon: <AlertTriangle className="w-4 h-4 text-orange-500" />,
        bg: 'bg-orange-500/10',
        border: 'border-orange-500/30',
      };
    case 'info':
      return {
        icon: <Info className="w-4 h-4 text-blue-500" />,
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
      };
    default:
      return {
        icon: <Info className="w-4 h-4 text-muted-foreground" />,
        bg: 'bg-muted/50',
        border: 'border-border',
      };
  }
}

export function AnalyseIATab({ chantierId, entrepriseId, chantierData }: AnalyseIATabProps) {
  const {
    analyse,
    derniereAnalyse,
    historique,
    limites,
    isLoading,
    loadingLimites,
    error,
    genererAnalyse,
    formatTempsRestant,
  } = useAnalyseIA(chantierId, entrepriseId);

  const { incrementAnalyseIA, canUseAnalyseIA } = useSubscription();

  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedAnalyseId, setSelectedAnalyseId] = useState<string | null>(null);
  const [showGlowAnimation, setShowGlowAnimation] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);

  // Countdown en temps réel pour le cooldown
  useEffect(() => {
    // Initialiser le countdown quand les limites changent
    if (limites && limites.minutesRestantes > 0) {
      setCooldownSeconds(Math.ceil(limites.minutesRestantes * 60));
    } else {
      setCooldownSeconds(0);
    }
  }, [limites?.minutesRestantes]);

  // Décrémenter le countdown chaque seconde
  useEffect(() => {
    if (cooldownSeconds <= 0) return;

    const interval = setInterval(() => {
      setCooldownSeconds(prev => {
        if (prev <= 1) {
          // Cooldown terminé, rafraîchir les limites
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownSeconds > 0]);

  // Composant countdown élégant
  const CountdownDisplay = ({ seconds, size = 'md' }: { seconds: number; size?: 'sm' | 'md' }) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    const isSmall = size === 'sm';
    const digitClass = isSmall
      ? "bg-slate-800 text-white px-1.5 py-0.5 rounded text-xs font-mono font-bold min-w-[24px] text-center"
      : "bg-slate-800 text-white px-2 py-1 rounded-md text-sm font-mono font-bold min-w-[32px] text-center";
    const labelClass = isSmall ? "text-[9px] text-slate-400 mt-0.5" : "text-[10px] text-slate-500 mt-1";
    const separatorClass = isSmall ? "text-slate-400 text-xs font-bold" : "text-slate-400 text-sm font-bold";

    return (
      <div className="flex items-center gap-1">
        {h > 0 && (
          <>
            <div className="flex flex-col items-center">
              <span className={digitClass}>{h.toString().padStart(2, '0')}</span>
              <span className={labelClass}>h</span>
            </div>
            <span className={separatorClass}>:</span>
          </>
        )}
        <div className="flex flex-col items-center">
          <span className={digitClass}>{m.toString().padStart(2, '0')}</span>
          <span className={labelClass}>min</span>
        </div>
        <span className={separatorClass}>:</span>
        <div className="flex flex-col items-center">
          <span className={digitClass}>{s.toString().padStart(2, '0')}</span>
          <span className={labelClass}>sec</span>
        </div>
      </div>
    );
  };

  // Animation glow au chargement si analyse disponible
  const canAnalyze = !isLoading && !loadingLimites && (!limites || limites.peutAnalyser) && cooldownSeconds === 0;

  useEffect(() => {
    // Déclencher l'animation une seule fois quand l'analyse devient disponible
    if (canAnalyze && !isLoading && analyse) {
      setShowGlowAnimation(true);
      // Désactiver après l'animation (1.5s)
      const timer = setTimeout(() => setShowGlowAnimation(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [canAnalyze, analyse]);

  // Vérifier si le quota mensuel est atteint (via SubscriptionContext)
  const isQuotaAtteint = limites && limites.quotaRestant !== undefined && limites.quotaRestant <= 0 && !canUseAnalyseIA();

  // Lancer l'analyse
  const handleGenerate = async () => {
    const result = await genererAnalyse(chantierData, 'manuelle');
    // Mettre à jour le compteur de quotas si l'analyse a réussi
    if (result) {
      await incrementAnalyseIA();
    }
  };

  // Vérifier si on a assez de données
  const hasMinimalData = chantierData?.montant_devis_ht > 0;

  // Calculer les KPIs côté client (plus fiable que d'attendre Claude)
  const calculerKPIs = () => {
    const devisHT = chantierData?.montant_devis_ht || 0;
    const totalDepenses = (chantierData?.reel_materiaux || 0) + (chantierData?.reel_main_oeuvre || 0) + (chantierData?.reel_frais || 0);
    const margeEuros = devisHT - totalDepenses;
    const margePct = devisHT > 0 ? (margeEuros / devisHT) * 100 : 0;

    // Utiliser les totaux calculés par useCalculations (plus fiables)
    const totalHeures = chantierData?.heures_totales_equipe ||
      chantierData?.equipes?.reduce((sum: number, e: any) => sum + (e.heures_total || 0), 0) || 0;

    const totalJours = chantierData?.jours_totaux_equipe ||
      chantierData?.equipes?.reduce((sum: number, e: any) => sum + (e.jours_travailles || 0), 0) || 0;

    // Utiliser jours_effectifs si jours_travailles n'est pas renseigné
    const joursEffectifs = chantierData?.jours_effectifs || 0;
    const joursUtilises = totalJours > 0 ? totalJours : joursEffectifs;

    // Rentabilité horaire = marge / heures travaillées
    const heuresUtilisees = totalHeures > 0 ? totalHeures : (joursUtilises * 8);
    const rentabiliteHoraire = heuresUtilisees > 0 ? margeEuros / heuresUtilisees : null;

    // Indice de performance = (devis - dépenses) / dépenses = ROI en ratio
    const indicePerformance = totalDepenses > 0 ? (margeEuros / totalDepenses) + 1 : (margePct >= 0 ? 1 + margePct/100 : 0);

    // Coût journalier moyen équipe (utiliser celui de useCalculations si dispo)
    const coutJournalier = chantierData?.cout_journalier_equipe ||
      (joursUtilises > 0 ? totalDepenses / joursUtilises : 0);

    return {
      rentabiliteHoraire,
      indicePerformance,
      coutJournalier,
      totalHeures: heuresUtilisees,
      totalJours: joursUtilises,
    };
  };

  const kpisCalcules = calculerKPIs();

  // État de chargement initial
  if (loadingLimites) {
    return (
      <Card className="border-primary/20">
        <CardContent className="pt-8 pb-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <Skeleton className="w-20 h-20 rounded-full" />
            <Skeleton className="w-48 h-6" />
            <Skeleton className="w-64 h-4" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // État loading première génération : animation logo
  if (!analyse && isLoading) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-purple-500/5">
        <CardContent className="pt-16 pb-16">
          <B8ildLogoLoader message="Génération de l'analyse IA" />
        </CardContent>
      </Card>
    );
  }

  // État vide - pas d'analyse
  if (!analyse) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-purple-500/5">
        <CardContent className="pt-16 pb-16 text-center">
          <div className="inline-flex p-4 bg-primary/10 rounded-full mb-6">
            <Brain className="w-16 h-16 text-primary" />
          </div>
          <h3 className="text-2xl font-bold mb-2">Analyse IA du chantier</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Générez une analyse complète de votre chantier par intelligence artificielle.
            Obtenez un score, des alertes et des recommandations personnalisées.
          </p>

          {/* Données insuffisantes */}
          {!hasMinimalData && (
            <Alert className="mb-6 text-left max-w-xl mx-auto border-orange-500/30 bg-orange-500/5">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              <AlertDescription className="text-sm">
                <strong>Données insuffisantes</strong> : Pour une analyse pertinente, renseignez d'abord un devis actif avec un montant.
              </AlertDescription>
            </Alert>
          )}

          {/* Limites affichées - uniquement le quota */}
          {limites && !isQuotaAtteint && (
            <div className="flex items-center justify-center gap-4 mb-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Target className="w-4 h-4" />
                <span>Quota : {limites.quotaUtilise}/{limites.quotaMax} ce mois</span>
              </div>
            </div>
          )}

          {/* Banner quota atteint avec upsell */}
          {isQuotaAtteint && (
            <div className="max-w-lg mx-auto mb-6">
              <QuotaExceededBanner type="analyse_ia" variant="banner" />
            </div>
          )}

          {/* Bouton générer ou countdown (masqué si quota atteint) */}
          {!isQuotaAtteint && (
            cooldownSeconds > 0 ? (
              <div className="flex flex-col items-center gap-2">
                <span className="text-xs text-white font-medium">Prochaine analyse disponible dans</span>
                <CountdownDisplay seconds={cooldownSeconds} size="md" />
              </div>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={isLoading || !hasMinimalData}
                size="lg"
                className="gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Générer l'analyse IA
                  </>
                )}
              </Button>
            )
          )}

          {!hasMinimalData && !isQuotaAtteint && (
            <p className="text-xs text-muted-foreground mt-3">
              Ajoutez un devis actif pour activer l'analyse
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Affichage de l'analyse
  const indicateurStyle = getIndicateurStyle(analyse.indicateur);
  const stars = analyse.etoiles || Math.round(analyse.score / 20);

  return (
    <div className="space-y-4 relative">
      {/* Overlay loading lors du refresh */}
      {isLoading && (
        <div className="absolute inset-0 z-10 bg-background/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
          <B8ildLogoLoader message="Actualisation de l'analyse" />
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          SYNTHÈSE PRINCIPALE
          ══════════════════════════════════════════════════════════════════ */}
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-purple-500/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Brain className="w-6 h-6 text-primary" />
              Analyse IA
            </CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="relative inline-block">
                    {/* Conteneur pour l'animation étoile filante autour de la bordure */}
                    {showGlowAnimation && (
                      <>
                        <span
                          className="absolute -inset-[2px] rounded-md pointer-events-none overflow-hidden"
                          style={{
                            background: 'conic-gradient(from var(--angle), transparent 0%, transparent 75%, #F59E0B 80%, #FCD34D 85%, transparent 90%, transparent 100%)',
                            animation: 'borderRotate 1.5s linear forwards',
                          }}
                        />
                        {/* Masque intérieur pour ne garder que la bordure */}
                        <span
                          className="absolute inset-[1px] rounded-[5px] pointer-events-none"
                          style={{ background: 'hsl(var(--card))' }}
                        />
                      </>
                    )}
                    {cooldownSeconds > 0 ? (
                      <div className="flex flex-col items-center gap-1 px-4 py-2 border border-white/30 rounded-lg bg-white/5">
                        <span className="text-[10px] text-white font-medium">Disponible dans</span>
                        <CountdownDisplay seconds={cooldownSeconds} size="sm" />
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGenerate}
                        disabled={isLoading || isQuotaAtteint}
                        className={cn(
                          "border border-white/50 relative",
                          showGlowAnimation && "border-transparent"
                        )}
                      >
                        {isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        <span>Nouvelle Analyse <span className="font-bold">B<span className="text-[#F59E0B]">8</span>ild</span> IA</span>
                      </Button>
                    )}
                  </span>
                </TooltipTrigger>
                {isQuotaAtteint && (
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="font-medium">Quota mensuel atteint</p>
                    <p className="text-xs text-muted-foreground">
                      Passez au plan supérieur pour continuer à utiliser l'analyse IA.
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent>
          {/* Score et verdict */}
          <div className="flex items-start gap-6">
            {/* Score circulaire */}
            <div className="relative flex-shrink-0">
              <div className={cn(
                'w-24 h-24 rounded-full flex items-center justify-center',
                indicateurStyle.bg,
                'border-4',
                analyse.score >= 70 ? 'border-green-500/50' :
                analyse.score >= 50 ? 'border-blue-500/50' :
                analyse.score >= 40 ? 'border-orange-500/50' : 'border-red-500/50'
              )}>
                <div className="text-center">
                  <span className="text-3xl font-black">{analyse.score}</span>
                  <span className="text-sm text-muted-foreground">/100</span>
                </div>
              </div>
            </div>

            {/* Verdict et étoiles */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={cn(indicateurStyle.bg, indicateurStyle.color, 'border-0')}>
                  {indicateurStyle.label}
                </Badge>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={cn(
                        'w-4 h-4',
                        i < stars ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'
                      )}
                    />
                  ))}
                </div>
              </div>
              <p className="text-sm text-foreground leading-relaxed">{analyse.verdict}</p>
              {derniereAnalyse && (
                <p className="text-xs text-muted-foreground mt-2">
                  Dernière analyse : {format(new Date(derniereAnalyse.created_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                </p>
              )}
            </div>
          </div>

          {/* Cartes santé financière et planning */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {/* Santé financière */}
            <div className={cn(
              'p-4 rounded-xl border',
              analyse.sante_financiere.statut === 'excellent' ? 'bg-green-500/5 border-green-500/20' :
              analyse.sante_financiere.statut === 'bon' ? 'bg-blue-500/5 border-blue-500/20' :
              analyse.sante_financiere.statut === 'attention' ? 'bg-orange-500/5 border-orange-500/20' :
              'bg-red-500/5 border-red-500/20'
            )}>
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="w-5 h-5 text-primary" />
                <span className="font-semibold">Santé financière</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Marge actuelle</span>
                  <span className="font-mono font-semibold">
                    {(analyse.sante_financiere.marge_actuelle_pct ?? 0).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">vs prévue</span>
                  <span className={cn(
                    'font-mono font-semibold flex items-center gap-1',
                    (analyse.sante_financiere.ecart_pts ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'
                  )}>
                    {(analyse.sante_financiere.ecart_pts ?? 0) >= 0 ? (
                      <TrendingUp className="w-3 h-3" />
                    ) : (
                      <TrendingDown className="w-3 h-3" />
                    )}
                    {(analyse.sante_financiere.ecart_pts ?? 0) >= 0 ? '+' : ''}
                    {(analyse.sante_financiere.ecart_pts ?? 0).toFixed(1)} pts
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">vs benchmark</span>
                  <Badge variant="outline" className="text-xs">
                    {analyse.sante_financiere.vs_benchmark}
                  </Badge>
                </div>
              </div>
              {analyse.sante_financiere.alerte && (
                <Alert className="mt-3 py-2 border-orange-500/30 bg-orange-500/5">
                  <AlertTriangle className="w-3 h-3 text-orange-500" />
                  <AlertDescription className="text-xs">
                    {analyse.sante_financiere.alerte}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Santé planning */}
            <div className={cn(
              'p-4 rounded-xl border',
              analyse.sante_planning.statut === 'en_avance' ? 'bg-green-500/5 border-green-500/20' :
              analyse.sante_planning.statut === 'a_lheure' ? 'bg-blue-500/5 border-blue-500/20' :
              analyse.sante_planning.statut === 'leger_retard' ? 'bg-orange-500/5 border-orange-500/20' :
              'bg-red-500/5 border-red-500/20'
            )}>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-5 h-5 text-primary" />
                <span className="font-semibold">Planning</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Avancement</span>
                  <span className="font-mono font-semibold">
                    {(analyse.sante_planning.avancement_pct ?? 0).toFixed(0)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Statut</span>
                  <div className="flex items-center gap-1.5">
                    {getPlanningIcon(analyse.sante_planning.statut)}
                    <span className="text-xs">
                      {analyse.sante_planning.ecart_jours > 0 ? `+${analyse.sante_planning.ecart_jours}j` :
                       analyse.sante_planning.ecart_jours < 0 ? `${analyse.sante_planning.ecart_jours}j` : 'À l\'heure'}
                    </span>
                  </div>
                </div>
                <Progress
                  value={Math.min(100, analyse.sante_planning.avancement_pct)}
                  className="h-2 mt-2"
                />
              </div>
              {analyse.sante_planning.alerte && (
                <Alert className="mt-3 py-2 border-orange-500/30 bg-orange-500/5">
                  <AlertTriangle className="w-3 h-3 text-orange-500" />
                  <AlertDescription className="text-xs">
                    {analyse.sante_planning.alerte}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {/* Graphique évolution du score IA (masqué si < 2 analyses) */}
          {historique && historique.length >= 2 && (
            <ScoreEvolutionChart
              data={historique}
              onPointClick={(id) => setSelectedAnalyseId(id)}
              className="mt-6"
            />
          )}

          {/* Points forts */}
          {analyse.points_forts && analyse.points_forts.length > 0 && (
            <div className="mt-6">
              <h4 className="font-semibold flex items-center gap-2 mb-3 text-green-600">
                <CheckCircle2 className="w-5 h-5" />
                Points forts
              </h4>
              <ul className="space-y-2">
                {analyse.points_forts.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Alertes */}
          {analyse.alertes && analyse.alertes.length > 0 && (
            <div className="mt-6">
              <h4 className="font-semibold flex items-center gap-2 mb-3 text-orange-600">
                <AlertTriangle className="w-5 h-5" />
                Alertes ({analyse.alertes.length})
              </h4>
              <div className="space-y-2">
                {analyse.alertes.map((alerte, i) => {
                  const style = getNiveauStyle(alerte.niveau);
                  return (
                    <div
                      key={i}
                      className={cn('p-3 rounded-lg border', style.bg, style.border)}
                    >
                      <div className="flex items-start gap-2">
                        {style.icon}
                        <div className="flex-1">
                          <p className="text-sm font-medium">{alerte.message}</p>
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Lightbulb className="w-3 h-3" />
                            {alerte.action_immediate}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION DÉPLIABLE : Recommandations, KPIs, Conseil
          ══════════════════════════════════════════════════════════════════ */}
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            <span>Voir les recommandations et KPIs</span>
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-4 mt-4">
          {/* Recommandations */}
          {analyse.recommandations && analyse.recommandations.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary" />
                  Recommandations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analyse.recommandations.map((reco, i) => (
                    <div key={i} className="p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={getPrioriteBadgeVariant(reco.priorite)} className="text-[10px]">
                          {reco.priorite}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">
                          {reco.categorie}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium">{reco.action}</p>
                      <p className="text-xs text-green-600 mt-1">
                        Impact : {reco.impact}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* KPIs */}
          {analyse.kpis && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  KPIs Dirigeant
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TooltipProvider>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                    {/* ROI */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-3 bg-muted/30 rounded-lg cursor-help">
                          <p className="text-xs text-muted-foreground mb-1">ROI</p>
                          <p className="text-xl font-bold font-mono">
                            {(analyse.kpis.roi_pct ?? 0).toFixed(1)}%
                          </p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="font-semibold">Retour sur Investissement</p>
                        <p className="text-xs text-muted-foreground">
                          (Marge / Dépenses) × 100<br/>
                          Mesure le gain généré par rapport aux dépenses engagées.
                        </p>
                      </TooltipContent>
                    </Tooltip>

                    {/* CA/jour */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-3 bg-muted/30 rounded-lg cursor-help">
                          <p className="text-xs text-muted-foreground mb-1">CA/jour</p>
                          <p className="text-xl font-bold font-mono">
                            {(analyse.kpis.ca_par_jour ?? 0).toFixed(0)}€
                          </p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="font-semibold">Chiffre d'affaires par jour</p>
                        <p className="text-xs text-muted-foreground">
                          Devis HT / Durée du chantier<br/>
                          Indique la valeur générée quotidiennement.
                        </p>
                      </TooltipContent>
                    </Tooltip>

                    {/* Performance */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-3 bg-muted/30 rounded-lg cursor-help">
                          <p className="text-xs text-muted-foreground mb-1">Performance</p>
                          <p className={cn(
                            'text-xl font-bold font-mono',
                            kpisCalcules.indicePerformance >= 1 ? 'text-green-500' : 'text-red-500'
                          )}>
                            {kpisCalcules.indicePerformance.toFixed(2)}x
                          </p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="font-semibold">Indice de performance</p>
                        <p className="text-xs text-muted-foreground">
                          (Marge + Dépenses) / Dépenses<br/>
                          &gt; 1x = rentable, &lt; 1x = perte.<br/>
                          Ex: 1.50x = vous gagnez 50% de plus que vos dépenses.
                        </p>
                      </TooltipContent>
                    </Tooltip>

                    {/* Rentabilité horaire */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-3 bg-muted/30 rounded-lg cursor-help">
                          <p className="text-xs text-muted-foreground mb-1">Rent. horaire</p>
                          <p className="text-xl font-bold font-mono">
                            {kpisCalcules.rentabiliteHoraire != null
                              ? `${kpisCalcules.rentabiliteHoraire.toFixed(1)}€/h`
                              : 'N/A'}
                          </p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="font-semibold">Rentabilité horaire</p>
                        <p className="text-xs text-muted-foreground">
                          Marge / Heures travaillées<br/>
                          {kpisCalcules.totalHeures > 0
                            ? `Basé sur ${kpisCalcules.totalHeures}h travaillées.`
                            : 'N/A = aucune heure renseignée pour l\'équipe.'}
                        </p>
                      </TooltipContent>
                    </Tooltip>

                    {/* Coût/jour */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-3 bg-muted/30 rounded-lg cursor-help">
                          <p className="text-xs text-muted-foreground mb-1">Coût/jour</p>
                          <p className="text-xl font-bold font-mono">
                            {kpisCalcules.coutJournalier.toFixed(0)}€
                          </p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="font-semibold">Coût journalier moyen</p>
                        <p className="text-xs text-muted-foreground">
                          Coût total équipe / Jours travaillés<br/>
                          {kpisCalcules.totalJours > 0
                            ? `Basé sur ${kpisCalcules.totalJours} jours travaillés.`
                            : 'Renseignez les jours travaillés de l\'équipe pour ce KPI.'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </CardContent>
            </Card>
          )}

          {/* Conseil expert */}
          {analyse.conseil_expert && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-4 pb-4">
                <div className="flex gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg h-fit">
                    <Brain className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-1">Conseil de l'expert</p>
                    <p className="text-sm text-muted-foreground italic">
                      "{analyse.conseil_expert}"
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Analyse des coûts */}
          {analyse.analyse_couts && (
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Wallet className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Analyse des coûts</span>
                </div>
                <p className="text-sm text-muted-foreground">{analyse.analyse_couts.analyse}</p>
                {analyse.analyse_couts.poste_problematique && (
                  <Badge variant="destructive" className="mt-2 text-xs">
                    Poste à surveiller : {analyse.analyse_couts.poste_problematique}
                  </Badge>
                )}
              </CardContent>
            </Card>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* ══════════════════════════════════════════════════════════════════
          INDICATEUR QUOTA / COOLDOWN
          ══════════════════════════════════════════════════════════════════ */}
      {/* Indicateur quota / cooldown - ou banner d'upsell si quota atteint */}
      {isQuotaAtteint ? (
        <QuotaExceededBanner type="analyse_ia" variant="inline" />
      ) : limites && (
        <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-2">
          <div className="flex items-center gap-1.5">
            <Target className="w-3 h-3" />
            <span>Quota : {limites.quotaUtilise}/{limites.quotaMax} ce mois</span>
          </div>
        </div>
      )}

      {/* Modal détail analyse historique */}
      <AnalyseHistoriqueModal
        analyseId={selectedAnalyseId}
        open={!!selectedAnalyseId}
        onClose={() => setSelectedAnalyseId(null)}
      />
    </div>
  );
}
